import type { GenericFormType } from '@neo/blocks/forms/types'
import type { ApiResponse } from '@shared/types'
import type { useForm } from '@tanstack/react-form'
import type { UseMutationResult } from '@tanstack/react-query'
import type { zodValidator } from '@tanstack/zod-form-adapter'
import type { Edge, Node, ReactFlowInstance } from '@xyflow/react'
import type {
  AgentChannels,
  DeleteService,
  FlowTypes,
  GetService,
  PhloType,
  PostService,
  RPhloPlaygroundInit,
} from 'v2/types'
import type { RFlows, RTimezone } from 'v2/types/api/settings'
import type z from 'zod'
import type { ZodCustomIssue } from 'zod'
import type { EvalEventName } from '../../utils/event-bus/eval-events'
import type { EvalProgressUpdate, EvalStatus, EvalStreamRequest } from './components/eval/types'

export type JsonPrimitive = string | number | boolean | null
export type JsonValue = JsonPrimitive | { [key: string]: JsonValue } | JsonValue[]

import type { McpTool, McpToolsResponse } from '../settings/types'
import type { ValidationError } from './components/validation-error-popover'
import type { ConversationSnapshot } from './components/vibe-agent/wire'
import type { AgentSettingsFormValues } from './config/base.config'
import type { actionsSchema, intentsSchema, v2validations } from './config/validation.config'
import type {
  AddDocumentPayload,
  CreateKBPayload,
  CreateKBResponse,
  McpArticle,
  McpArticleCollection,
  RCrawlSiteStatus,
  RKnowledgeBase,
  SyncDocumentPayload,
  SyncSourcePayload,
  WebsiteScraperPayload,
} from './knowledge-base/types'
import type { FlowRunEvent } from './run-info/types'
import type {
  CollectionSuggestPayload,
  RCollectionSuggestion,
} from './sidebar/ai-agents/collection-configuration-types'
import type {
  GenerateCodeParams,
  GenerateCodeResponse,
  GenerateFunctionSchemaParams,
  GenerateFunctionSchemaResponse,
  GenerateHttpFunctionSchemaParams,
  RExecHttpRequest,
  RLanguage,
  RMapActionParameters,
  RMapActionParametersPayload,
  RProviders,
  RSlackChannel,
  RVoiceWithModelAndProvider,
  SecretVariable,
  SlackChannelPayload,
  TextToSpeechPayload,
} from './sidebar/types'
import type { LeaderProfile } from './utils/system-one-thinking'

export type AiAssistNodeData = InferFromValidations<FlowNodeTypes.ai_assist>

// biome-ignore lint/suspicious/noExplicitAny: TODO - fix type definition
export type MutateType = UseMutationResult<any, any, any>['mutate']

// ** Important Enum for FLOW ** //
// This enum is used to define the types of nodes that can be added to the flow
export enum FlowNodeTypes {
  start = 'start',
  multi_party_call_v2 = 'multi_party_call_v2',
  conference_bridge = 'conference_bridge',
  stream = 'stream',
  agent_preset_conversation = 'agent_preset_conversation',
  ai_skills = 'ai_skills',
  end_conversation = 'end_conversation',
  ai_agent_v2 = 'ai_agent_v2',
  // Shares every panel and rule with ai_agent_v2 — see utils/ai-agent-v29.ts
  ai_agent_v29 = 'ai_agent_v29',
  agent_node = 'agent_node',
  contact_screening = 'contact_screening',
  outbound_screening = 'outbound_screening',
  ai_action = 'ai_action',
  initiate_call = 'initiate_call',
  note = 'note',
  ai_assist = 'ai_assist',
  user_input = 'user_input',
  ai_agent_call = 'ai_agent_call',
  ai_agent_message = 'ai_agent_message',
  ai_agent_chat = 'ai_agent_chat',
  ai_agent_whatsapp = 'ai_agent_whatsapp',
  branch_v2 = 'branch_v2',
  send_whatsapp = 'send_whatsapp',
  send_chat = 'send_chat',
  send_message = 'send_message',
  multi_party_call_outbound = 'multi_party_call_outbound',
  whatsapp_conversation = 'whatsapp_conversation',
  message_conversation = 'message_conversation',
  business_hour = 'business_hour',
  app_action = 'app_action',
  http_request = 'http_request',
  create_task = 'create_task',
  counter = 'counter',
  queue_and_route = 'queue_and_route',
  customer_feedback = 'customer_feedback',
  call_forward = 'call_forward',
  get_input = 'get_input',
  voicemail = 'voicemail',
  prompt = 'prompt',
  ivr_menu = 'ivr_menu',
  menu = 'menu',
  select_trigger = 'select_trigger',
  hangup = 'hangup',
}

// Special node ID for system prompt sidebar
export const SYSTEM_PROMPT_NODE_ID = '__system_prompt__'

// System Prompt Configuration
export interface SystemPrompt {
  prompt: string
  ai_nodes: string[]
  all_nodes_enabled?: boolean
}

// Global Meta Configuration - contains all global flow settings
interface GlobalMetaFields {
  system_prompt: SystemPrompt | null
  stt_guidance?: string | null
  agent_name?: string | null
  agent_persona?: string | null
  livekit_ready?: boolean
}
export type GlobalMeta = GlobalMetaFields | null

export enum StartNodeTypes {
  start = 'start',
  start_api_request = 'api_request',
  incoming_call = 'incoming_call',
  incoming_chat = 'incoming_chat',
  incoming_slack = 'incoming_slack',
}

export enum ActionFieldType {
  AUTO = 'auto',
  MANUAL = 'manual',
}

export type GenericNode = Node<NodeDataBase<unknown>>
export type GenericEdge = Edge<EdgeData>
export type RFInstance = ReactFlowInstance<GenericNode, GenericEdge>
export type ReactFlowDataObject<N extends Node = Node, E extends Edge = Edge> = {
  nodes: N[]
  edges: E[]
}
export type AgentMeta = {
  defaultModelConfig?: {
    model: string
    provider: string
  }
  defaultChannel?: AgentChannels
}
// Store related types
export enum EditorType {
  USER = 'user',
  VIBE_AGENT = 'vibe_agent',
}

// Who owns a node's name: 'user' = pinned by the user, AI must not rename;
// absent/undefined = AI-managed. Single source for the wire + FE contract.
export type NameSource = 'user'

// Context captured when a call AI conversation add is blocked, so the
// confirmation dialog can build the source -> Initiate Call -> AI conversation
// chain at the exact spot the node would have been added.
export interface CallTriggerPromptData {
  sourceNodeId?: string
  isStandalone: boolean
  isDrag: boolean
  // Source-node output handle the new node would hang off (empty for AI sources;
  // an intent handle is created on confirm instead).
  sourceHandle: string
  position: { x: number; y: number }
  // Palette type the user picked, so the dialog rebuilds the same node.
  nodeType?: FlowNodeTypes
}

export interface FlowState {
  selectedNodeID: string | null
  flowInstance: RFInstance | null // the instance of the flow, use it rarely
  unsavedModalOpen: boolean
  isEditing: boolean
  showTriggerDialog: boolean
  showTriggerChangeConfirmationModal: boolean
  triggerChangeData: {
    oldTriggerType: FlowTypes | null
    newTriggerType: FlowTypes | null
  } | null
  callTriggerPrompt: CallTriggerPromptData | null
  showVibeSidebar: boolean
  vibeSidebarWidth: number
  showEvalSidebar: boolean
  agentSettingsActiveTab: string // active tab in agent settings sidebar
  sidebarFormInstance: null | ReturnType<
    // biome-ignore lint/suspicious/noExplicitAny: TODO - fix type definition
    typeof useForm<any, ReturnType<typeof zodValidator>>
  >
  agentSettings: AgentSettingsFormValues | null
  initialAgentSettings: AgentSettingsFormValues | null
  tabFormInstances: Partial<
    Record<
      'settings' | 'voice-config' | 'event-callbacks',
      { state: { values: unknown }; options: { defaultValues?: unknown } } | null
    >
  >
  candidateNode: Node<NodeDataBase<unknown>> | null
  isSelectionModeActive: boolean
  currentFlowType: PhloType | null
  agentMeta: AgentMeta
  glowingNodeID: string | null
  isValidationErrorPopoverOpen: boolean
  systemPrompt: SystemPrompt | null
  isGlobalMetaLoaded: boolean
  sttGuidance: string | null
  agentName: string | null
  agentPersona: string | null
  leaderProfile: LeaderProfile | null
  currentFlowName: string | null
  // True once the user manually renames in this builder session; gates the Vibe Agent's
  // FLOW_NAME_SUGGESTED so it can't overwrite a user-chosen name. Cleared on store reset.
  flowNameUserEdited: boolean
  shouldStreamFlowNameEffect: boolean
  showNodeSelectionModal: boolean
  lastEditorType: EditorType | null
  vibeAgentContributed: boolean
  hasSettingsValidationError: boolean
  requestedPath: string | null
  // Incremented to ask the node sidebar to focus the name field (context-menu Rename).
  // A nonce instead of a boolean so repeated Rename on the already-open node re-triggers.
  nameFocusNonce: number
  // V2.9 nodes whose Inputs tab has already been seeded from the instructions in this
  // builder session. The sidebar remounts on every save, and re-seeding would put back
  // rows the author deleted. Not persistable: the backend projects config.model keys.
  inputsSeededNodeIds: Set<string>
  /** V2.9 nodes whose save already asked which Inputs the Task calls for, and the names proposed. */
  inputsProposed: Map<string, Set<string>>
  /** V2.9 nodes whose save could not ask for proposals, so the next save asks again. */
  inputsProposalOwed: Set<string>
  // V2.9 Inputs: the state of the node's save-time collection-detection pass.
  collectionDetection: {
    /** The id of the node whose pass is in flight; another node is not busy because of it. */
    running: string | null
    errors: Record<string, string>
    failure: string | null
    /** Not a failure, e.g. this agent runner cannot propose Inputs yet. */
    notice: string | null
  }
}

export interface EvalStreamEntry {
  id: string
  type: EvalEventName
  payload: unknown
  receivedAt: string
}

export interface FlowActions {
  selectNode: (nodeID: string) => void
  setCandidateNode: (node: Node<NodeDataBase<unknown>> | null) => void
  unselectNode: (save?: boolean) => void
  reset: () => void
  setFlowInstance: (instance: RFInstance | null) => void
  setUnsavedModalOpen: (open: boolean) => void
  discardChanges: () => void
  setSidebarFormInstance: (instance: FlowState['sidebarFormInstance']) => void
  requestNameFieldFocus: () => void
  setIsEditing: (isEditing: boolean, nodeId?: string | null) => void
  setAgentSettingsActiveTab: (tab: string) => void
  setShowVibeSidebar: (show: boolean) => void
  setVibeSidebarWidth: (width: number) => void
  setShowEvalSidebar: (show: boolean) => void
  setAgentSettings: (settings: AgentSettingsFormValues) => void
  setInitialAgentSettings: (settings: AgentSettingsFormValues) => void
  setTabFormInstance: (
    key: 'settings' | 'voice-config' | 'event-callbacks',
    form: { state: { values: unknown }; options: { defaultValues?: unknown } } | null,
  ) => void
  toggleSelectionMode: () => void
  setShowTriggerDialog: (show: boolean) => void
  setShowTriggerChangeConfirmationModal: (
    show: boolean,
    data?: {
      oldTriggerType: FlowTypes | null
      newTriggerType: FlowTypes | null
    },
  ) => void
  setCallTriggerPrompt: (data: CallTriggerPromptData | null) => void
  setCurrentFlowType: (type?: PhloType) => void
  setAgentMeta: (meta: AgentMeta) => void
  setGlowingNodeID: (id: string | null) => void
  toggleValidationErrorPopover: () => void
  setSystemPrompt: (systemPrompt: SystemPrompt | null, selectNode?: boolean) => void
  setIsGlobalMetaLoaded: (isLoaded: boolean) => void
  setSttGuidance: (sttGuidance: string | null) => void
  setAgentName: (agentName: string | null) => void
  setAgentPersona: (agentPersona: string | null) => void
  setLeaderProfile: (profile: LeaderProfile | null) => void
  setCurrentFlowName: (name: string | null) => void
  setFlowNameUserEdited: (value: boolean) => void
  setShouldStreamFlowNameEffect: (shouldStream: boolean) => void
  setShowNodeSelectionModal: (show: boolean) => void
  markVibeAgentChange: () => void
  markUserChange: () => void
  markVibeAgentChangeForNodes: (nodeIds: string[]) => void
  resetEditorTracking: () => void
  getEditorMetadata: () => { last_editor_type: EditorType; vibe_agent_contributed: boolean }
  setHasSettingsValidationError: (hasError: boolean) => void
  setRequestedPath: (path: string | null) => void
  markInputsSeeded: (nodeId: string) => void
  markInputsProposed: (nodeId: string, names: string[]) => void
  markInputsProposalOwed: (nodeId: string) => void
  setCollectionDetection: (patch: Partial<FlowState['collectionDetection']>) => void
  writeDetection: (nodeId: string, patch: Partial<FlowState['collectionDetection']>) => void
}

// uuid and timezzone are added for b hours in backend
export type OutputState = {
  id: string
  name: string
  default: boolean
  uuid?: string
  timezone?: RTimezone
  selected?: boolean
  meta?: Record<string, unknown>
  // Customer-facing one-liner shown on hover (port rows + edge chips); only
  // fixed-outcome nodes (screening) carry it — user-authored intents don't.
  description?: string
  // Section label grouping fixed outcomes in the Select-a-Path picker
  // ("Unanswered" / "Answered" on screening nodes). Display-only.
  section?: string
}
export type OutputStates = Record<FlowNodeTypes, OutputState[]>

export interface SidebarComponentProps<T extends Record<string, unknown>> {
  selectedNode: Node<T>
  unselectNode: (save?: boolean) => void
  nodeVars: string[]
}

// Add any additional data for the node here, which is only required by the UI
interface NodeMeta {
  name: string
  issues?: ZodCustomIssue[]
  type?: FlowNodeTypes
  shouldHighlight?: boolean
  evalStatus?: EvalStatus
  evalPassedCount?: number
  evalTotalCount?: number
  last_editor_type?: EditorType
  // id of the AI node whose reply-timeout branch auto-created this target;
  // lets queued canvas updates recognize the branch before React Flow flushes
  noReplyTargetOf?: string
}

export type NodeDataBase<T = undefined> = {
  meta: NodeMeta
  // TEMPORARY WORKAROUND (v3 edit-lock; remove when the task compiler is re-enabled):
  // Server-derived compiled task graph (v3 runtime). Present on ai_agent_v2 nodes
  // once the meta_compiler has run; lives in node.config['_meta'] server-side and is
  // surfaced read-only so the console can gate edit/publish on v3 flows. Kept OUTSIDE
  // `config` so it is never round-tripped back to the backend on save.
  _meta?: { tasks: Array<Record<string, unknown>> } | null
  // 'user' = the node name was set by the user and AI metadata generation must not
  // rename it; absent = AI-managed. Kept OUTSIDE `config` because data.config is
  // serialized into config.model, whose keys are whitelist-projected by the backend
  // on GET — a flag inside it would be silently dropped. Persisted as a sibling of
  // config.model instead (see transformFlowData / inverseTransformFlowData).
  name_source?: NameSource
  // Start node also has a configuration where trigger is api request, we can add payload_format and this also passed as nodevars to components below
  config: T & {
    name: string
    description?: string
    payload_format?: Record<string, unknown> | string
  }
}

export type EdgeData = {
  isHovered?: boolean
  nodeVars: string[]
  meta?: {
    issues?: ZodCustomIssue[]
  }
  isChosenPath?: boolean
}

export enum AgentState {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  PAUSED = 'PAUSED',
}
export interface RequestConfigPayload {
  url: string
  method: 'POST' | 'GET'
  auth: 'NONE' | 'BASIC' | 'BEARER'
  username?: string
  password?: string
  token?: string
}

export interface ConversationGoal {
  id?: number
  uuid?: string
  goal_name: string
  goal_instructions: string
}

export interface FlowConfig {
  name: string
  group: string
  enabled: boolean
  id?: string
  url?: string
  description?: string
  redact?: boolean
  changed?: boolean
  state: AgentState
  // `null` under the unavailable marker below (config #345 sends null, not omitted), `string[]` otherwise.
  knowledge_base_ids?: string[] | null
  // SER-5976 (config #345): response-only marker set truthy when the backend could NOT resolve this
  // flow's linked KB uuids (rag-uuid resolution failed) — as opposed to the flow genuinely owning 0
  // KBs. Mirrors the reverse-read's null/linksUnavailable contract. When truthy, `knowledge_base_ids`
  // above is UNKNOWN (a transient-empty placeholder), NOT authoritative: never render it as "0 KBs"
  // and never round-trip it into a save (that would WIPE the links — see use-publish-flow). Never sent
  // back to the backend.
  knowledge_base_ids_unavailable?: boolean
  /** Subaccount this agent is tagged to (SA…), or null when untagged. */
  sub_auth_id?: string | null
  voice_ai_config?: AgentSettingsFormValues['voice_ai_config']
  phlo_type?: FlowTypes
  is_legacy?: boolean
  /** False only for migrated legacy flows (still owned by the old console). Duplicates of legacy flows are legacy but editable. */
  is_editable?: boolean
  /** Root legacy flow uuid this flow descends from (self for migrated flows). */
  legacy_flow_id?: string
  /** Immediate source flow uuid when this is a duplicate of a legacy flow. */
  duplicated_from?: string
  is_default?: boolean
  event_callbacks?: AgentSettingsFormValues['event_callbacks']
  schedule?: AgentSettingsFormValues['schedule']
  updated_at?: number
  conversation_goals?: ConversationGoal[]
  created_by?: {
    uuid: string
    name: string
    email: string
  }
}

// FE Flow data - used by React Flow
export interface FlowData {
  nodes: Node<NodeDataBase<unknown>>[]
  edges: Edge<EdgeData>[]
  phlo: FlowConfig
  global_meta?: GlobalMeta
  validation_errors?: ValidationError[]
}

// BE Flow data - data that is stored in server and sent to it
export interface RFlowPayload {
  connections: RConnection[]
  nodes: ROutputNode[]
  phlo: FlowConfig
  notes: {
    notes: {
      id: string
      title: string
      content: string
      color: string
      x: number
      y: number
      width: number
      height: number
      zIndex: number
    }[]
  } | null
  validation_errors?: unknown
  global_meta: GlobalMeta
}
export type DataItem = {
  data: unknown
  inputType?: string
  key?: string
  label?: string
  type: string
  validation?: string
  output_states?: OutputState[]
}

export interface ROutputNode {
  id: string
  name: string
  type: string
  description?: string
  top: number
  left: number
  component: FlowNodeTypes
  changed: boolean
  last_editor_type?: EditorType
  config: {
    model: DataItem[]
    output_states?: OutputState[]
    // TEMPORARY WORKAROUND (v3 edit-lock; remove when the task compiler is re-enabled):
    // server-derived compiled task graph (v3 runtime), surfaced read-only by config-service.
    _meta?: { tasks: Array<Record<string, unknown>> } | null
    // User-pinned node name marker. Stored as a sibling of `model` because model keys
    // are whitelist-projected by the backend on GET; config-service re-injects this key
    // the same way it re-injects `_meta`.
    name_source?: NameSource
  }
}

export interface RConnection {
  data: {
    id: string
    type?: string
  }
  source: string // Formatted as "nodeId.sourceHandle"
  target: string // Formatted as "nodeId.Input"
}

export enum HandleType {
  Source = 'source',
  Target = 'target',
}
type InferFromValidations<T extends keyof ReturnType<typeof v2validations>> = NodeDataBase<
  z.infer<
    ReturnType<typeof v2validations>[T] extends z.ZodType
      ? ReturnType<typeof v2validations>[T]
      : never
  >
>

export interface Expression {
  case_sensitive: boolean
  lhs: string
  operation: string
  rhs: string[]
}

export type PromptItem = z.infer<ReturnType<typeof v2validations>['promptItem']>

export type Action = z.infer<typeof actionsSchema>
export type Intent = z.infer<typeof intentsSchema>

export type AiAgentNodeData =
  | AiAgentChatNodeData
  | AiAgentWhatsappNodeData
  | AiAgentMessageNodeData
  | AiAgentCallNodeData

// Node metadata generation types
export interface ExistingNodeSummary {
  name: string
  config: object
}

export interface GenerateNodeMetadataRequest {
  nodeType: string
  config: object
  existingNodes?: ExistingNodeSummary[]
  existingName?: string
  existingDescription?: string
}

export interface GenerateNodeMetadataResponse {
  node_name: string
  node_description: string | null
}

// Flow Run Status Enum
export enum FlowRunStatus {
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  IN_PROGRESS = 'IN_PROGRESS',
  QUEUED = 'QUEUED',
  EXPIRED = 'EXPIRED',
}

// Paginated Flows Response
export interface RFlowsPaginated {
  flows: RFlows[]
  total: number
}

export interface CostComponent {
  name: string
  cost: number
  duration: number
  rate: number
}

export interface ConversationMetrics {
  answered: boolean
  bot_detected: boolean
  bot_reason: string
  bot_technical_reason: string
  cx_voicemail_detected: boolean
  hallucination_detected: boolean
  instructions_adherence_failed: boolean
  intent_not_found: boolean
  intent_wrongly_identified: boolean
  node_loop_detected: boolean
  silent_call: boolean
  user_interest: string
  user_interest_reason: string
  user_interest_technical_reason: string
  user_sentiment: string
  user_sentiment_reason: string
  user_sentiment_technical_reason: string
  variable_extraction_failed: boolean
  voicemail_detected: boolean
  voicemail_detection_failed: boolean
  voicemail_reason: string
  voicemail_technical_reason: string
}

export interface NodeEvaluation {
  hallucination: {
    hallucinated: boolean
    score?: number
    reason: string
    technical_reason: string
  }
  instructions_adherence: {
    adherence_passed: boolean
    score?: number
    reason: string
    technical_reason: string
  }
  intent_identification: {
    intent_not_found: boolean
    intent_wrongly_identified: boolean
    score?: number
    reason: string
    technical_reason: string
  }
  node_loop: {
    loop_detected: boolean
    score?: number
    reason: string
    suspected_issue: string
    technical_reason: string
  }
  node_name: string
  node_run_key: string
  node_run_uuid: string
  node_uuid: string
  turn_count?: number
  variable_extraction: {
    extraction_successful: boolean
    score?: number
    missing_variables: string[]
    reason: string
    required_variables: string[]
    technical_reason: string
  }
}

interface NodeMetrics {
  node_evaluations: NodeEvaluation[]
}

export interface GoalEvaluation {
  goal_name: string
  goal_achieved: boolean
  /** pass | fail | unknown — present for metric-backed rows; absent falls back to goal_achieved. */
  verdict?: 'pass' | 'fail' | 'unknown'
  reason: string
}

export interface GoalMetrics {
  goal_evaluations: GoalEvaluation[]
}

export interface RFlowRun {
  auth_id: string
  /** Subaccount this run acted/billed as (SA…), or null when untagged. */
  sub_auth_id?: string | null
  conversation_id: string | null
  concurrent_limit_applied: boolean
  duration: number
  ended_at: number
  is_playground: boolean
  phlo_active_version_id: number
  phlo_name: string
  phlo_run_uuid: string
  phlo_type: string
  phlo_uuid: string
  started_at: number
  started_by?: {
    email: string
    name: string
    uuid: string
  }
  status: FlowRunStatus
  call_uuid: string[] | null
  recording_id: string[] | null
  total_cost: number
  cost_components?: CostComponent[]
  conversation_metrics?: ConversationMetrics
  node_metrics?: NodeMetrics
  goal_metrics?: GoalMetrics | null
}

export interface RFlowRunsPaginated {
  runs: RFlowRun[]
  total: number
}

export interface FlowRunDetailsResponse {
  run: RFlowRun
  logs: Array<FlowRunEvent> // FlowRunEvent from run-info/types
}

export interface RPhloMetaStatus {
  ready: boolean
  pending: Array<{ uuid: string; name: string }>
  total_ai_nodes: number
}

export interface RPhloNodeMeta {
  node_uuid: string
  name: string
  meta: { tasks: Array<Record<string, unknown>> } | null
  meta_hash: string | null
}

// SER-5591 flow versioning. `id` is the phlo_connections.id row key used for
// preview/switch; `version` is the human-facing display integer.
export interface FlowVersion {
  id: number
  version: number
  is_active: boolean
  edited_by: string | null
  is_vibe: boolean
  notes: string | null
  created_at: number // Unix timestamp in SECONDS (backend sends int(ts.timestamp()))
  // git-style change message: change_summary = <=10-word "title", change_description =
  // longer plain-English "body". Both NULL while pending (filled async a few seconds after
  // save), or permanently for pre-feature / non-agent versions. (SER-5645)
  change_summary: string | null
  change_description: string | null
}

export interface FlowVersionsResponse {
  active_version_id: number
  versions: FlowVersion[]
}

export interface SwitchVersionResponse {
  active_version_id: number
  version: number
}

export interface FlowService {
  getLanguages: GetService<{ languages: Array<RLanguage> }>
  listVersions: GetService<FlowVersionsResponse, [string]>
  switchVersion: (
    phloID: string,
    sourceVersionId: number,
  ) => Promise<ApiResponse<SwitchVersionResponse>>
  editVersionSummary: (
    phloID: string,
    versionId: number,
    summary: string,
    // undefined = omit the field from the request (stored description untouched)
    description?: string,
  ) => Promise<
    ApiResponse<{ success: boolean; change_summary: string; change_description: string | null }>
  >
  listFlowsPaginated: GetService<RFlowsPaginated, [Record<string, unknown>]>
  listFlowRuns: GetService<RFlowRunsPaginated, [Record<string, unknown>]>
  listFlowRunsPaginated: GetService<RFlowRunsPaginated, [Record<string, unknown>]>
  getFlowRunDetails: GetService<FlowRunDetailsResponse, [string, 'ao'?]>
  getProviders: GetService<RProviders, [string | undefined]>
  getFlowConfig: GetService<RFlowPayload, [string, string?]>
  upsertFlow: PostService<RFlowPayload, RFlowPayload>
  updateFlowSettings: PostService<RFlowPayload, AgentSettingsFormValues, [string]>
  deleteFlow: DeleteService<void>
  execHttpRequest: PostService<RExecHttpRequest>
  generateFunctionSchema: PostService<GenerateFunctionSchemaResponse, GenerateFunctionSchemaParams>
  generateHttpFunctionSchema: PostService<
    GenerateFunctionSchemaResponse,
    GenerateHttpFunctionSchemaParams
  >
  mapActionParameters: PostService<RMapActionParameters, RMapActionParametersPayload>
  generateCode: PostService<GenerateCodeResponse, GenerateCodeParams>
  generateIntentName: (
    instruction: string,
    existingIntents?: Array<{
      intent_name: string
      intent_instructions: string
    }>,
  ) => Promise<{ data: { intent_name: string } }>
  generateNodeMetadata: PostService<GenerateNodeMetadataResponse, GenerateNodeMetadataRequest>
  // the runner answers with the bare body (no `data` envelope), so this is not a PostService
  suggestCollectionConfiguration: (
    payload: CollectionSuggestPayload,
  ) => Promise<RCollectionSuggestion>
  generateGoals: (
    id: string,
    conversationGoals: ConversationGoal[],
  ) => Promise<{
    data: { goals: Array<{ goal_name: string; goal_instructions: string }> }
  }>
  listKnowledgeBases: GetService<RKnowledgeBase[]>
  createKnowledgeBase: PostService<CreateKBResponse, CreateKBPayload>
  crawlSite: PostService<{ job_id: string }, WebsiteScraperPayload>
  getCrawlSiteStatus: GetService<RCrawlSiteStatus, [string]>
  cancelCrawl: PostService<{ cancelled: boolean }>

  getDocumentsFromSource: GetService<{ fetch_id: string }, [string, string, boolean?]>
  getDocumentsFromSourceByFetchId: GetService<
    { status: string; data: McpArticleCollection[] | McpArticle[] },
    [string]
  >
  getDocumentFromSource: GetService<McpArticle, [string, string, string]>
  // Lazy picker: one collection's (e.g. Confluence space's) pages, fetched on expand (capped; has_more
  // means the space has more pages than returned).
  getCollectionArticles: GetService<
    { articles: McpArticle[]; hasMore: boolean },
    [string, string, string]
  >
  deleteKnowledgeBase: DeleteService<void>
  updateKnowledgeBase: PostService<RKnowledgeBase, Partial<RKnowledgeBase>, [string]>
  addDocument: PostService<RKnowledgeBase, AddDocumentPayload, [string]>
  getKnowledgeBase: GetService<RKnowledgeBase, [string]>
  syncKnowledgeBase: PostService<{ id: string }>
  syncKnowledgeBaseDocument: PostService<void, SyncDocumentPayload>
  syncKnowledgeBaseSource: PostService<void, SyncSourcePayload>
  deleteKnowledgeBaseSource: DeleteService<void, { sourceId: string; kbId: string }>
  deleteDocument: DeleteService<void, { documentId: string; kbId: string }>
  updateDocument: PostService<RKnowledgeBase, Partial<RKnowledgeBase>, [string, string]>
  getVoices: GetService<Array<RVoiceWithModelAndProvider>, [string, string?]>
  textToSpeech: PostService<{ pronunciation_preview_url: string }, TextToSpeechPayload>
  listSecretVariables: GetService<SecretVariable[]>
  deleteSecretVariable: DeleteService<void, string>
  createSecretVariable: PostService<
    SecretVariable,
    { name: string; value: string; description: string }
  >
  updateSecretVariable: PostService<
    SecretVariable,
    string,
    [{ name: string; value: string; description: string }]
  >
  getSlackChannels: PostService<RSlackChannel, SlackChannelPayload>
  getSlackCreateAppLink: GetService<{ app_creation_url: string }>
  resetVibeConversation: PostService<
    { new_session_id: string },
    { phlo_uuid: string; session_id: string }
  >
  getVibeAgentConversation: GetService<ConversationSnapshot, [string]>
  getMcpTools: GetService<McpToolsResponse>
  getMcpToolsForOrgAppUuid: GetService<McpTool, [string, string]>
  // duplicateFlow: (id: string, newFlowName: string) => Promise<RFlowPayload>
  initializePlayground: PostService<RPhloPlaygroundInit, string>
  getPhloMetaStatus: GetService<RPhloMetaStatus, [string]>
  getPhloNodeMeta: GetService<RPhloNodeMeta, [string, string]>
  triggerApiRequest: (
    authId: string,
    flowId: string,
    queryParams?: Record<string, string | number | boolean>,
    body?: Record<string, JsonValue>,
    isLegacy?: boolean,
  ) => Promise<ApiResponse<void>>
  triggerStartFlowRunner: (
    authId: string,
    phloId: string,
    queryParams?: Record<string, string | number | boolean>,
    body?: Record<string, JsonValue>,
  ) => Promise<ApiResponse<void>>
  startEvalStream: (
    body: EvalStreamRequest,
    handlers: {
      onMessage: (data: EvalProgressUpdate) => void
      onError: (error: unknown) => void
      onEvent?: (event: string | undefined, data: EvalProgressUpdate) => void
      onDone?: () => void
    },
  ) => AbortController
  cancelEval: (evalId: string) => Promise<ApiResponse<void>>
  listSimulationScenarios: (phloUuid: string, page?: number, pageSize?: number) => Promise<unknown>
  generateSimulationScenarios: (
    body: Record<string, unknown>,
    handlers: {
      onMessage: (data: unknown) => void
      onError: (error: unknown) => void
      onEvent?: (event: string | undefined, data: unknown) => void
      onDone?: () => void
    },
  ) => AbortController
  deleteSimulationScenario: (uuid: string) => Promise<unknown>
  deleteAllSimulationScenarios: (phloUuid: string) => Promise<unknown>
  batchDeleteSimulationScenarios: (uuids: string[]) => Promise<unknown>
  runSelectedScenariosStream: (
    body: Record<string, unknown>,
    handlers: {
      onMessage: (data: unknown) => void
      onError: (error: unknown) => void
      onEvent?: (event: string | undefined, data: unknown) => void
      onDone?: () => void
    },
  ) => AbortController
  listSimulationRuns: (phloUuid: string, page?: number, pageSize?: number) => Promise<unknown>
  getSimulationRun: (runUuid: string) => Promise<unknown>
  getSimulationRunScenario: (runUuid: string, scenarioUuid: string) => Promise<unknown>
  renameSimulationRun: (runUuid: string, name: string) => Promise<unknown>
  exportSimulationRun: (runUuid: string) => Promise<unknown>
  postVibeFeedback: (
    sessionId: string,
    body: { response_id: string; thumbs_up: boolean | null; comment: string | null },
  ) => Promise<ApiResponse<VibeFeedbackResponse>>
  cancelWorkflowStream: (body: {
    session_id: string
    response_id: string
  }) => Promise<ApiResponse<{ cancelled: boolean }>>
  // Voice Cloning
  cloneVoice: (formData: FormData) => Promise<ApiResponse<VoiceCloningResponse>>
  getCommunityVoices: GetService<RCommunityVoices, [Record<string, unknown>]>
}

export enum VoiceType {
  INSTANT = 'instant',
  PROFESSIONAL = 'professional',
  COMMUNITY = 'community',
}

export interface VoiceCloningResponse {
  voice_id: string
  label: string
  description?: string
  gender?: string
}

export interface CommunityVoice {
  voice_id: string
  name: string
  gender: string
  accent: string
  age: string
  language: string
  description: string
  preview_url: string
  category: string
  use_case: string
  public_owner_id: string
}

export interface RCommunityVoices {
  voices: CommunityVoice[]
  has_more: boolean
  page: number
  page_size: number
}

// Voice Preview Handler Types
export interface VoicePreviewParams {
  voiceId: string
  previewUrl: string | null
  model?: string
  provider?: string
  languageCode?: string
}

export type VoicePreviewHandler = (params: VoicePreviewParams) => Promise<void>

export type AiAgentChatNodeformData = AiAgentChatNodeData['config']
export type AiAgentWhatsappNodeformData = AiAgentWhatsappNodeData['config']
export type AiAgentMessageNodeformData = AiAgentMessageNodeData['config']
export type AiAgentCallNodeformData = AiAgentCallNodeData['config']

export type AiAgentNodeformData =
  | AiAgentChatNodeformData
  | AiAgentWhatsappNodeformData
  | AiAgentMessageNodeformData
  | AiAgentCallNodeformData

export type AgentPresetConversationNodeformData = AgentPresetConversationNodeData['config']
export type AiAgentFormData = {
  form: GenericFormType<AiAgentNodeformData>
}
// Various default data types in our flow
// Follow the same naming pattern - [FlowNodeTypes.component_type]: Component[NodeData]
// Notice the "NodeData" suffix in the Type name
export type StartNodeData = InferFromValidations<FlowNodeTypes.start>
export type NoteNodeData = InferFromValidations<FlowNodeTypes.note>
export type AppActionNodeData = InferFromValidations<FlowNodeTypes.app_action>
export type HttpRequestNodeData = InferFromValidations<FlowNodeTypes.http_request>
export type BusinessHourNodeData = InferFromValidations<FlowNodeTypes.business_hour>

export type CreateTaskNodeData = InferFromValidations<FlowNodeTypes.create_task>
export type CounterNodeData = InferFromValidations<FlowNodeTypes.counter>
export type QueueAndRouteNodeData = InferFromValidations<FlowNodeTypes.queue_and_route>
export type CustomerFeedbackNodeData = InferFromValidations<FlowNodeTypes.customer_feedback>
export type CallForwardNodeData = InferFromValidations<FlowNodeTypes.call_forward>
export type GetInputNodeData = InferFromValidations<FlowNodeTypes.get_input>
export type VoicemailNodeData = InferFromValidations<FlowNodeTypes.voicemail>
export type PromptNodeData = InferFromValidations<FlowNodeTypes.prompt>
export type IVRNodeData = InferFromValidations<FlowNodeTypes.ivr_menu>
export type HangupNodeData = InferFromValidations<FlowNodeTypes.hangup>
export type AiAgentChatNodeData = InferFromValidations<FlowNodeTypes.ai_agent_chat>
export type AiAgentWhatsappNodeData = InferFromValidations<FlowNodeTypes.ai_agent_whatsapp>
export type AiAgentMessageNodeData = InferFromValidations<FlowNodeTypes.ai_agent_message>
export type AiAgentCallNodeData = InferFromValidations<FlowNodeTypes.ai_agent_call>
export type BranchV2NodeData = InferFromValidations<FlowNodeTypes.branch_v2>
export type SendMessageNodeData = InferFromValidations<FlowNodeTypes.send_message>
export type SendChatNodeData = InferFromValidations<FlowNodeTypes.send_chat>
export type SendWhatsappNodeData = InferFromValidations<FlowNodeTypes.send_whatsapp>
export type MessageConversationNodeData = InferFromValidations<FlowNodeTypes.message_conversation>
export type WhatsappConversationNodeData = InferFromValidations<FlowNodeTypes.whatsapp_conversation>
export type MultiPartyCallOutboundNodeData =
  InferFromValidations<FlowNodeTypes.multi_party_call_outbound>
export type UserInputNodeData = InferFromValidations<FlowNodeTypes.user_input>
// -- Add new node types here --
export type MultiPartyCallV2NodeData = InferFromValidations<FlowNodeTypes.multi_party_call_v2>

export type ConferenceBridgeNodeData = InferFromValidations<FlowNodeTypes.conference_bridge>
export type StreamNodeData = InferFromValidations<FlowNodeTypes.stream>

export type AgentPresetConversationNodeData =
  InferFromValidations<FlowNodeTypes.agent_preset_conversation>
export type AiSkillsNodeData = InferFromValidations<FlowNodeTypes.ai_skills>

export type EndConversationNodeData = InferFromValidations<FlowNodeTypes.end_conversation>
export type AiAgentV2NodeData = InferFromValidations<FlowNodeTypes.ai_agent_v2>
export type AgentNodeNodeData = InferFromValidations<FlowNodeTypes.agent_node>
export type ContactScreeningNodeData = InferFromValidations<FlowNodeTypes.contact_screening>
export type OutboundScreeningNodeData = InferFromValidations<FlowNodeTypes.outbound_screening>

export type AiActionNodeData = InferFromValidations<FlowNodeTypes.ai_action>

export type InitiateCallNodeData = InferFromValidations<FlowNodeTypes.initiate_call>

export type FlowUpsertResponse = {
  validation_errors?: unknown
  validation_warning?: string
  phlo?: {
    id?: string
    conversation_goals?: ConversationGoal[]
    schedule?: AgentSettingsFormValues['schedule']
  }
}

export interface VibeFeedbackResponse {
  session_id: string
  response_id: string
  thumbs_up: boolean | null
  comment: string | null
}
